<template>
  <UmisisModal
    ref="customDeadlineModal"
    :ok-disabled="isUpdating || !customDeadlineValue"
    title="Set Custom Deadline"
    @ok="submitCustomDeadline"
    @show="onModalShow"
  >
    <Field>
      <FieldLabel for="custom-deadline-datetime">
        Deadline date and time
      </FieldLabel>
      <Input id="custom-deadline-datetime" v-model="customDeadlineValue" type="datetime-local" />
      <FieldDescription>Select the exact deadline date and time.</FieldDescription>
    </Field>
  </UmisisModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../../resources/js/components/ui/input/index.js'
import { useTasksStore } from '../../../stores/tasksStore.js'
import { formatTaskFieldDateTimeForToast, toTaskDateTimeLocalValue } from '../../../utils/taskFieldDateTime.js'

const props = defineProps({
  taskId: {
    type: [Number, String],
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
})

const customDeadlineValue = ref('')
const { componentRef: customDeadlineModal, openModal: openDialog, closeModal: closeDialog } = useModalControl()

const tasksStore = useTasksStore(props.context)
const normalizedTaskId = computed(() => Number(props.taskId))
const isUpdating = computed(() => tasksStore.isUpdatingDeadline(normalizedTaskId.value))

const openModal = () => {
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const updateTaskDeadline = async (deadlineAt, successMessage = 'Deadline updated.') => {
  if (!Number.isInteger(normalizedTaskId.value) || normalizedTaskId.value <= 0) {
    return false
  }

  try {
    await tasksStore.updateDeadline(normalizedTaskId.value, deadlineAt)
    toast.success(successMessage)
    return true
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.deadline_at?.[0]
      || 'Unable to update the deadline.'

    toast.error(errorMessage)
    return false
  }
}

const onModalShow = () => {
  customDeadlineValue.value = toTaskDateTimeLocalValue(props.value)
}

const submitCustomDeadline = async (event) => {
  event?.preventDefault?.()

  if (!customDeadlineValue.value) {
    return
  }

  const selectedDate = new Date(customDeadlineValue.value)

  if (Number.isNaN(selectedDate.getTime())) {
    toast.error('Please provide a valid date and time.')
    return
  }

  const formattedDeadline = formatTaskFieldDateTimeForToast(selectedDate.toISOString())
  const didUpdate = await updateTaskDeadline(
    selectedDate.toISOString(),
    formattedDeadline ? `Deadline set to ${formattedDeadline}.` : 'Custom deadline updated.'
  )

  if (didUpdate) {
    closeDialog()
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>
